import {forwardRef, useEffect, useMemo, useState} from 'react';
import {TableVirtuoso} from 'react-virtuoso';
import 'dayjs/locale/en';

import {
    useAddProfileAbtestsMutation,
    useAddProfileCampaignsMutation, useGetProfileAbtestsQuery,
    useGetProfileCampaignsQuery, useRemoveProfileAbtestsMutation,
    useRemoveProfileCampaignsMutation, useRestartProfileAbtestsMutation,
    useRestartProfileCampaignsMutation, useUpdateProfileAbtestsMutation, useUpdateProfileCampaignsMutation
} from "../../core/reducers/profile";
import Loading from "../Loading";
import {filterProfileCampaigns} from "./profileCampaignFilters";
import {
    Table,
    TableContainer,
    MenuItem,
    InputAdornment,
    Typography,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    CircularProgress,
    Box, Alert, Collapse
} from "@mui/material";
import {formatDate, isProdAdmin} from "../Utils/CommonUtils";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import CachedIcon from '@mui/icons-material/Cached';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from "@mui/icons-material/Close";
import {DateTimePicker} from "@mui/x-date-pickers/DateTimePicker";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {useSelector} from "react-redux";

const timezone = require('dayjs/plugin/timezone');
const utc = require('dayjs/plugin/utc');
dayjs.extend(timezone);
dayjs.extend(utc);

// Stable slot components preserve Virtuoso measurements between profile updates.
const profileTableComponents = {
    Scroller: forwardRef(function ProfileScroller({context, ...props}, ref) {
        return <TableContainer {...props} ref={ref} />;
    }),
    Table: ({context, children, ...props}) => <Table {...props} size="small" sx={{
        width: '100%', borderCollapse: 'separate', tableLayout: 'auto',
        '& .MuiTableCell-root': {px: {xs: 1, md: 2}},
    }}>
        <colgroup>
            <col style={{width: context?.hasActions ? '27%' : '31%'}} />
            <col style={{width: context?.hasActions ? '12%' : '14%'}} />
            <col style={{width: context?.hasActions ? '11%' : '12%'}} />
            <col style={{width: context?.hasActions ? '11%' : '12%'}} />
            <col style={{width: context?.hasActions ? '11%' : '12%'}} />
            <col style={{width: context?.hasActions ? '11%' : '12%'}} />
            <col style={{width: '7%'}} />
            {context?.hasActions && <col style={{width: '10%'}} />}
        </colgroup>
        {children}
    </Table>,
    TableHead: forwardRef(function ProfileTableHead({context, ...props}, ref) {
        return <TableHead {...props} ref={ref} sx={{
            '& .MuiTableCell-head': {whiteSpace: 'nowrap', py: 1, borderBottom: '1px solid #e5ebef'},
            '& .profile-header-groups .MuiTableCell-head': {
                bgcolor: '#edf3f5', color: '#5c7082', typography: 'caption',
                fontWeight: 'fontWeightBold', letterSpacing: '0.08em', textTransform: 'uppercase',
            },
            '& .profile-header-columns .MuiTableCell-head': {
                bgcolor: '#f8fafb', color: '#8292a1', typography: 'caption', fontWeight: 'fontWeightMedium',
            },
        }} />;
    }),
    TableBody: forwardRef(function ProfileTableBody({context, ...props}, ref) {
        return <TableBody {...props} ref={ref} sx={{
            '& .MuiTableCell-body': {py: {xs: 1, md: 1.5}, borderBottom: '1px solid #e9eef1'},
        }} />;
    }),
    TableRow: ({context, item, ...props}) => <TableRow {...props} sx={{'&:hover': {bgcolor: '#f8fafb'}}} />,
    EmptyPlaceholder: ({context}) => <tbody>
        <TableRow>
            <TableCell colSpan={context.columnCount} align="center">{context.emptyMessage}</TableCell>
        </TableRow>
    </tbody>,
};

const ProfileDateCell = ({timestamp}) => {
    const [date, time] = formatDate(timestamp * 1000, 'YYYY-MM-DD HH:mm:ss', 0).split(' ');
    return <TableCell sx={{whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums'}}>
        <Box sx={{typography: 'body2', fontWeight: 'fontWeightMedium', color: '#344255'}}>{date}</Box>
        <Box sx={{typography: 'caption', color: '#8999a8'}}>{time}</Box>
    </TableCell>;
};

export const ProfileCampaign = ({userId, env = 'liveops', type = 'campaign', refreshKey}) => {

    const isCampaign = type === 'campaign';
    const [filterText, setFilterText] = useState('');
    const [campaignType, setCampaignType] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const invalidDates = !!(dateFrom && dateTo && dateFrom > dateTo);

    // Queries
    const campaignQuery = useGetProfileCampaignsQuery(
        {env: env, userId: userId, p:refreshKey},
        {skip: !isCampaign || userId === '' || userId === undefined}
    );
    const abtestQuery = useGetProfileAbtestsQuery(
        {env: env, userId: userId, p:refreshKey},
        {skip: isCampaign || userId === '' || userId === undefined}
    );

    const [removeProfileCampaign, removeProfileCampaignResponse] = useRemoveProfileCampaignsMutation();
    const [addProfileCampaign, addProfileCampaignResponse] = useAddProfileCampaignsMutation();
    const [restartProfileCampaign, restartProfileCampaignResponse] = useRestartProfileCampaignsMutation();
    const [updateProfileCampaign, updateProfileCampaignResponse] = useUpdateProfileCampaignsMutation();

    const [removeProfileAbtest, removeProfileAbtestResponse] = useRemoveProfileAbtestsMutation();
    const [addProfileAbtest, addProfileAbtestResponse] = useAddProfileAbtestsMutation();
    const [restartProfileAbtest, restartProfileAbtestResponse] = useRestartProfileAbtestsMutation();
    const [updateProfileAbtest, updateProfileAbtestResponse] = useUpdateProfileAbtestsMutation();

    const { data, isLoading, isFetching, isError, error } = isCampaign ? campaignQuery : abtestQuery;
    const removeResponse = isCampaign ? removeProfileCampaignResponse : removeProfileAbtestResponse;
    const addResponse = isCampaign ? addProfileCampaignResponse : addProfileAbtestResponse;
    const restartResponse = isCampaign ? restartProfileCampaignResponse : restartProfileAbtestResponse;
    const updateResponse = isCampaign ? updateProfileCampaignResponse : updateProfileAbtestResponse;

    // Состояния для диалоговых окон
    const [dialogState, setDialogState] = useState({
        open: false,
        type: null, // 'edit', 'delete', 'restart'
        itemId: null,
        cohortId: null,
        inputValue: '',

        startSec: null,
        endSec: null,
        mainPhaseFromSec: null,
        mainPhaseTillSec: null
    });

    const [alertState, setAlertState] = useState({
        open: false,
        severity: 'success', // 'success', 'error', 'warning', 'info'
        message: ''
    });


    const entityName = isCampaign ? 'Campaign' : 'AB Test';
    const idFieldLabel = isCampaign ? 'Offer ID' : 'AB Test ID';


    // Открытие диалога
    const handleOpenDialog = (type, campaign) => {
        const newDialogState = {
            ...dialogState,
            open: true,
            type,
            itemId: campaign?.id,
            inputValue: campaign?.id || ''
        }


        if(type == 'edit') {
            newDialogState.startSec = campaign.start
            newDialogState.endSec = campaign.end
            newDialogState.mainPhaseFromSec = campaign.mainPhaseFrom
            newDialogState.mainPhaseTillSec = campaign.mainPhaseTill
        }
        if(!isCampaign) newDialogState.cohortId = campaign.cohort

        setDialogState(newDialogState);
    };

    const handleCloseAlert = () => {
        setAlertState(prev => ({...prev, open: false}));
    };

    // Закрытие диалога
    const handleCloseDialog = () => {
        setDialogState({
            open: false,
            type: null,
            itemId: null,
            inputValue: '',

            startSec: null,
            endSec: null,
            mainPhaseFromSec: null,
            mainPhaseTillSec: null,
            cohortId:null
        });
    };

    // Обработка подтверждения
    const handleConfirm = () => {
        const { type: dialogType, itemId, inputValue } = dialogState;

        const params = {
            userId: userId,
            env: env
        };

        if (isCampaign) {
            params.offerId = dialogType === 'edit'  || dialogType === 'create' ? inputValue : itemId;
        } else {
            params.abTestId = dialogType === 'edit' || dialogType === 'create' ? inputValue : itemId;
            if(dialogType === 'create') params.cohortId = dialogState.cohortId
        }

        if(dialogType === 'edit') {
            params.startSec = parseInt(dialogState.startSec)
            params.endSec = parseInt(dialogState.endSec)
            params.mainPhaseFromSec = parseInt(dialogState.mainPhaseFromSec)
            params.mainPhaseTillSec = parseInt(dialogState.mainPhaseTillSec)

            if(!isCampaign) {
                params.cohort = dialogState.cohortId
            }
        }


        switch (dialogType) {
            case 'create':
                if (isCampaign) {
                    addProfileCampaign(params);
                } else {
                    debugger
                    addProfileAbtest(params);
                }
                break;
            case 'edit':
                debugger
                if (isCampaign) {
                    updateProfileCampaign(params);
                } else {
                    updateProfileAbtest(params);
                }
                break;
            case 'delete':
                if (isCampaign) {
                    removeProfileCampaign(params);
                } else {
                    removeProfileAbtest(params);
                }
                break;
            case 'restart':
                if (isCampaign) {
                    restartProfileCampaign(params);
                } else {
                    restartProfileAbtest(params);
                }
                break;
            default:
                break;
        }

        handleCloseDialog();
    };

    // Рендер диалогового окна
    const renderDialog = () => {
        const { type, inputValue, cohortId } = dialogState;

        const dialogConfig = {
            create: {
                title: `Creating new ${entityName.toLowerCase()}`,
                content: (
                    <>
                        <TextField
                            autoFocus
                            margin="dense"
                            label={idFieldLabel}
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={inputValue}
                            onChange={(e) => setDialogState(prev => ({...prev, inputValue: e.target.value}))}
                        />
                        {!isCampaign&&<TextField
                            margin="dense"
                            label={"Cohort Id"}
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={cohortId}
                            onChange={(e) => setDialogState(prev => ({...prev, cohortId: e.target.value}))}
                        />}
                    </>
                ),
                confirmText: 'ADD',
                confirmColor: 'primary'
            },
            edit: {
                title: `Editing ${entityName.toLowerCase()}`,
                content: (<>
                        {!isCampaign&&<TextField
                            margin="dense"
                            label={"Cohort Id"}
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={cohortId}
                            onChange={(e) => setDialogState(prev => ({...prev, cohortId: e.target.value}))}
                        />}
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
                            <DateTimePicker
                                label="startSec"
                                value={dialogState.startSec ? dayjs.unix(dialogState.startSec).utc() : null}
                                onChange={(newValue) => {
                                    const sec = newValue && newValue.isValid() ? newValue.utc().unix() : null;
                                    setDialogState(prev => ({...prev, startSec: sec}));
                                }}
                                format="YYYY-MM-DD HH:mm:ss"
                                ampm={false}
                                timezone="UTC"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        margin: "dense"
                                    }
                                }}
                            />
                            <DateTimePicker
                                label="endSec"
                                value={dialogState.endSec ? dayjs.unix(dialogState.endSec).utc() : null}
                                onChange={(newValue) => {
                                    const sec = newValue && newValue.isValid() ? newValue.utc().unix() : null;
                                    setDialogState(prev => ({...prev, endSec: sec}));
                                }}
                                format="YYYY-MM-DD HH:mm:ss"
                                ampm={false}
                                timezone="UTC"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        margin: "dense"
                                    }
                                }}
                            />
                            <DateTimePicker
                                label="mainPhaseFromSec"
                                value={dialogState.mainPhaseFromSec ? dayjs.unix(dialogState.mainPhaseFromSec).utc() : null}
                                onChange={(newValue) => {
                                    const sec = newValue && newValue.isValid() ? newValue.utc().unix() : null;
                                    setDialogState(prev => ({...prev, mainPhaseFromSec: sec}));
                                }}
                                format="YYYY-MM-DD HH:mm:ss"
                                ampm={false}
                                timezone="UTC"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        margin: "dense"
                                    }
                                }}
                            />
                            <DateTimePicker
                                label="mainPhaseTillSec"
                                value={dialogState.mainPhaseTillSec ? dayjs.unix(dialogState.mainPhaseTillSec).utc() : null}
                                onChange={(newValue) => {
                                    const sec = newValue && newValue.isValid() ? newValue.utc().unix() : null;
                                    setDialogState(prev => ({...prev, mainPhaseTillSec: sec}));
                                }}
                                format="YYYY-MM-DD HH:mm:ss"
                                ampm={false}
                                timezone="UTC"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        margin: "dense"
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </>
                ),
                confirmText: 'Save',
                confirmColor: 'primary'
            },
            delete: {
                title: `Delete ${entityName.toLowerCase()}`,
                content: `You want to delete ${isCampaign ? 'this campaign' : 'this AB-test'}?`,
                confirmText: 'Yes',
                confirmColor: 'error'
            },
            restart: {
                title: `Restart ${entityName.toLowerCase()}`,
                content: `You are going to restart ${isCampaign ? 'campaign' : 'AB-test'}?`,
                confirmText: 'Yes',
                confirmColor: 'warning'
            }
        };


        const config = dialogConfig[type] || {};

        return (
            <Dialog open={dialogState.open} onClose={handleCloseDialog}>
                <DialogTitle>{config.title}</DialogTitle>
                <DialogContent>
                    {config.content}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} color={config.confirmColor} variant="contained">
                        {config.confirmText}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // Проверка загрузки мутаций
    const isMutationLoading = removeProfileCampaignResponse.isLoading ||
        addProfileCampaignResponse.isLoading ||
        restartProfileCampaignResponse.isLoading;

    useEffect(() => {
        if (addResponse.isSuccess) {
            setAlertState({
                open: true,
                severity: 'success',
                message: `${entityName} saved ${isCampaign ? '' : ''}`
            });
        }
        if (addResponse.isError) {
            const errorData = addResponse.error?.data;
            setAlertState({
                open: true,
                severity: 'error',
                message: errorData?.error || `Error ${entityName.toLowerCase()}`
            });
        }
    }, [addResponse, entityName, isCampaign]);

    useEffect(() => {
        if (removeResponse.isSuccess) {
            setAlertState({
                open: true,
                severity: 'success',
                message: `${entityName} deleted ${isCampaign ? '' : ''}`
            });
        }
        if (removeResponse.isError) {
            const errorData = removeResponse.error?.data;
            setAlertState({
                open: true,
                severity: 'error',
                message: errorData?.error || `Error while deleting ${entityName.toLowerCase()}`
            });
        }
    }, [removeResponse, entityName, isCampaign]);

    useEffect(() => {
        if (restartResponse.isSuccess) {
            setAlertState({
                open: true,
                severity: 'success',
                message: `${entityName} restarted ${isCampaign ? '' : ''}`
            });
        }
        if (restartResponse.isError) {
            const errorData = restartResponse.error?.data;
            setAlertState({
                open: true,
                severity: 'error',
                message: errorData?.error || `Error while restarting ${entityName.toLowerCase()}`
            });
        }
    }, [restartResponse, entityName, isCampaign]);

    const campaigns = useMemo(() => Object.values(
        (isCampaign ? data?.campaignMap : data?.abTestDtoMap) || {}
    ), [data, isCampaign]);
    const campaignTypes = useMemo(() => [...new Set(campaigns.map(campaign => campaign.campaignType).filter(Boolean))].sort(), [campaigns]);
    const filteredCampaigns = useMemo(() => filterProfileCampaigns(campaigns, {
        campaignType: isCampaign ? campaignType : '', filterText, from: dateFrom, to: dateTo,
    }), [campaigns, isCampaign, campaignType, filterText, dateFrom, dateTo]);

    const userSession = useSelector((state) => state.data.UserSession);

    return <Box sx={{height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0}}>
        <Box sx={{mb: alertState.open ? 2 : 0, flexShrink: 0}}>
            <Collapse in={alertState.open}>
                <Alert
                    severity={alertState.severity}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={handleCloseAlert}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                >
                    {alertState.message}
                </Alert>
            </Collapse>
        </Box>

        <Box sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', '& > div': {flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}}>
        <Loading isLoading={isLoading||isFetching} isError={isError} error={error}>
            {data !== undefined && <>

                <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, pt: 0.5, pb: 1, flexShrink: 0}}>
                    <TextField
                        size="small"
                        label={isCampaign ? 'Campaign name or type' : 'AB Test name'}
                        value={filterText}
                        onChange={event => setFilterText(event.target.value)}
                        sx={{flex: '1 1 220px'}}
                        InputProps={{startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>}}
                    />
                    {isCampaign && <TextField
                        select
                        size="small"
                        label="Campaign type"
                        value={campaignType}
                        onChange={event => setCampaignType(event.target.value)}
                        sx={{width: 180}}
                    >
                        <MenuItem value="">All types</MenuItem>
                        {campaignTypes.map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                    </TextField>}
                    <TextField
                        type="date"
                        size="small"
                        label="Active from (UTC)"
                        value={dateFrom}
                        onChange={event => setDateFrom(event.target.value)}
                        InputLabelProps={{shrink: true}}
                        error={invalidDates}
                    />
                    <TextField
                        type="date"
                        size="small"
                        label="Active until (UTC)"
                        value={dateTo}
                        onChange={event => setDateTo(event.target.value)}
                        InputLabelProps={{shrink: true}}
                        error={invalidDates}
                        helperText={invalidDates ? 'End date must be on or after start date' : undefined}
                    />
                    <Button disabled={!filterText && !campaignType && !dateFrom && !dateTo} onClick={() => {
                        setFilterText('');
                        setCampaignType('');
                        setDateFrom('');
                        setDateTo('');
                    }}>Reset filters</Button>
                    <Typography variant="body2" sx={{ml: 'auto', whiteSpace: 'nowrap'}}>
                        {filteredCampaigns.length} / {campaigns.length}
                    </Typography>
                    <Button variant="contained" onClick={() => handleOpenDialog('create', '')}>ADD</Button>
                </Box>

                <Box position="relative" sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
                    {/* Оверлей загрузки */}
                    {isMutationLoading && (
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bgcolor="rgba(255, 255, 255, 0.8)"
                            zIndex={1000}
                        >
                            <CircularProgress />
                        </Box>
                    )}

                    <TableVirtuoso
                        key={JSON.stringify([campaignType, filterText, dateFrom, dateTo])}
                        style={{height: '100%'}}
                        data={filteredCampaigns}
                        components={profileTableComponents}
                        computeItemKey={(_, campaign) => campaign.id}
                        increaseViewportBy={{top: 200, bottom: 400}}
                        context={{
                            columnCount: isProdAdmin(env, userSession) ? 8 : 7,
                            hasActions: isProdAdmin(env, userSession),
                            emptyMessage: campaigns.length ? 'No entries match the filters' : 'No entries in this profile',
                        }}
                        fixedHeaderContent={() => (
                            <>
                                <TableRow className="profile-header-groups">
                                    <TableCell>{isCampaign ? 'Campaign' : 'AB Test'}</TableCell>
                                    <TableCell>{isCampaign ? 'Type' : 'Assignment'}</TableCell>
                                    <TableCell colSpan={2}>Active period (UTC)</TableCell>
                                    <TableCell colSpan={2}>Main phase (UTC)</TableCell>
                                    <TableCell>Restarts</TableCell>
                                    {isProdAdmin(env, userSession) && <TableCell>Actions</TableCell>}
                                </TableRow>
                                <TableRow className="profile-header-columns">
                                    <TableCell>ID</TableCell>
                                    <TableCell>{isCampaign ? 'Campaign type' : 'Cohort'}</TableCell>
                                    <TableCell>Start</TableCell>
                                    <TableCell>End</TableCell>
                                    <TableCell>From</TableCell>
                                    <TableCell>Until</TableCell>
                                    <TableCell>#</TableCell>
                                    {isProdAdmin(env, userSession) && <TableCell />}
                                </TableRow>
                            </>
                        )}
                        itemContent={(_, campaign) => (
                            <>
                                <TableCell sx={{typography: 'body2', fontWeight: 'fontWeightMedium', color: '#263445', whiteSpace: 'nowrap'}}>{campaign.id}</TableCell>
                                <TableCell sx={{typography: 'body2', color: '#47586a', overflowWrap: 'anywhere'}}>
                                    {isCampaign ? campaign.campaignType : campaign.cohort}
                                </TableCell>
                                <ProfileDateCell timestamp={campaign.start} />
                                <ProfileDateCell timestamp={campaign.end} />
                                <ProfileDateCell timestamp={campaign.mainPhaseFrom} />
                                <ProfileDateCell timestamp={campaign.mainPhaseTill} />
                                <TableCell sx={{fontVariantNumeric: 'tabular-nums'}}>{campaign.restartNumber}</TableCell>

                                {(isProdAdmin(env, userSession))&&<TableCell sx={{whiteSpace: 'nowrap'}}>
                                    <IconButton
                                        size="small"
                                        aria-label="edit"
                                        color="warning"
                                        onClick={() => handleOpenDialog('edit', campaign)}
                                        disabled={isMutationLoading}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        aria-label="restart"
                                        color="warning"
                                        onClick={() => handleOpenDialog('restart', campaign)}
                                        disabled={isMutationLoading}
                                    >
                                        <CachedIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        aria-label="delete"
                                        color="error"
                                        onClick={() => handleOpenDialog('delete', campaign)}
                                        disabled={isMutationLoading}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>}
                            </>
                        )}
                    />
                </Box>

                {renderDialog()}
            </>}
        </Loading>
        </Box>
    </Box>
}
